import React, { useEffect } from 'react'
import { useIntl } from 'react-intl'

import { Button, Form, notification } from 'antd'
import PropTypes from 'prop-types'
import * as R from 'ramda'

import useRequestHook from '../hooks/useRequestHook'

export default function FormComponent(props) {
  const {
    path,
    layout = 'vertical',
    confirmLabel = 'cta.confirm',
    confirmType = 'primary',
    confirmBlock = false,
    successMessage = 'messages.general_success',
    paramsDecorator = R.identity,
    paramsValidator = R.T,
    onResponseSuccess,
    onResponseError,
    onResponse = R.T,
    submitProps = {},
    formRef,
    fieldWatcher
  } = props
  const intl = useIntl()
  const [antdForm] = Form.useForm()
  const request = useRequestHook()

  useEffect(() => {
    if (!formRef) return
    formRef.current = { antdForm }
  }, [formRef, antdForm])

  /**
   * @function onSuccess
   */
  const onSuccess = onResponseSuccess
    ? onResponseSuccess
    : () => {
        antdForm.resetFields()
        notification.success({
          message: intl.formatMessage({ id: successMessage })
        })
      }

  /**
   * @function onError
   */
  const onError = onResponseError
    ? onResponseError
    : (response) => {
        console.error('ERRORS', response)
        if (!response) {
          notification.error({
            message: intl.formatMessage({ id: 'generic.error' })
          })
          return
        }
        const fieldsKeys = Object.keys(antdForm.getFieldsValue())
        const errorsWithFields = response.errors.filter((e) =>
          fieldsKeys.includes(e.code)
        )
        const errorsWithoutFields = response.errors.filter(
          (x) => !errorsWithFields.includes(x)
        )
        antdForm.setFields(
          errorsWithFields.map((err) => ({
            name: err.code,
            errors: [err.message]
          }))
        )

        if (errorsWithoutFields.length > 0) {
          notification.error({ message: errorsWithoutFields[0].message })
        }
      }

  /**
   * @function onFinish
   * @param {*} formParams
   */
  const onFinish = (formParams) => {
    const params = Object.assign({}, paramsDecorator(formParams))
    if (!paramsValidator(params)) return false

    request
      .send(path, { params })
      .then((response) => {
        if (!onResponse(response)) return
        onSuccess(response)
      })
      .catch((response) => {
        onError(response)
      })
  }

  return (
    <Form
      form={antdForm}
      layout={layout}
      onFinish={onFinish}
      onFieldsChange={fieldWatcher}
    >
      {props.children}

      <Form.Item style={{ textAlign: 'right', marginTop: 15 }}>
        <Button
          type={confirmType}
          block={confirmBlock}
          loading={request.loading}
          htmlType='submit'
          {...submitProps}
        >
          {intl.formatMessage({ id: confirmLabel })}
        </Button>
      </Form.Item>
    </Form>
  )
}

FormComponent.propTypes = {
  path: PropTypes.string.isRequired,
  layout: PropTypes.string,
  confirmLabel: PropTypes.string,
  confirmType: PropTypes.string,
  confirmBlock: PropTypes.bool,
  successMessage: PropTypes.string,
  paramsDecorator: PropTypes.func,
  paramsValidator: PropTypes.func,
  onResponseSuccess: PropTypes.func,
  onResponseError: PropTypes.func,
  onResponse: PropTypes.func,
  submitProps: PropTypes.object,
  fieldWatcher: PropTypes.func
}
